/**
 * Performance optimization utilities for ContentCard components
 * Provides caching strategies and performance monitoring for content cards with people photos
 */

import { ContentCard, PersonSummaryWithPhoto } from '../types';
import { globalImageCache } from './imageOptimization';

export interface ContentCardPerformanceConfig {
  enablePeoplePhotoPreloading: boolean;
  maxPreloadedCards: number;
  preloadThreshold: number; // Distance from viewport to start preloading
  enableMemoryOptimization: boolean;
}

export const DEFAULT_CONTENT_CARD_CONFIG: ContentCardPerformanceConfig = {
  enablePeoplePhotoPreloading: true,
  maxPreloadedCards: 10,
  preloadThreshold: 200, // pixels
  enableMemoryOptimization: true,
};

/**
 * Optimizes content cards for better performance
 */
export class ContentCardOptimizer {
  private config: ContentCardPerformanceConfig;
  private preloadedCards = new Set<string>();
  private observer: IntersectionObserver | null = null;

  constructor(config: Partial<ContentCardPerformanceConfig> = {}) {
    this.config = { ...DEFAULT_CONTENT_CARD_CONFIG, ...config };
    this.initializeObserver();
  }

  private initializeObserver(): void {
    if (typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const cardId = entry.target.getAttribute('data-card-id');
          if (!cardId) return;

          if (entry.isIntersecting) {
            this.handleCardVisible(cardId);
          } else {
            this.handleCardHidden(cardId);
          }
        });
      },
      {
        rootMargin: `${this.config.preloadThreshold}px`,
        threshold: 0.1,
      }
    );
  }

  private handleCardVisible(cardId: string): void {
    // Mark card as visible for potential preloading
    if (!this.preloadedCards.has(cardId) && this.preloadedCards.size < this.config.maxPreloadedCards) {
      this.preloadedCards.add(cardId);
    }
  }

  private handleCardHidden(cardId: string): void {
    // Optionally clean up resources for hidden cards
    if (this.config.enableMemoryOptimization) {
      this.preloadedCards.delete(cardId);
    }
  }

  /**
   * Observes a content card for performance optimization
   */
  public observeCard(element: Element, cardId: string): void {
    if (this.observer) {
      element.setAttribute('data-card-id', cardId);
      this.observer.observe(element);
    }
  }

  /**
   * Stops observing a content card
   */
  public unobserveCard(element: Element): void {
    if (this.observer) {
      this.observer.unobserve(element);
    }
  }

  /**
   * Preloads people photos for a content card
   */
  public async preloadCardPeoplePhotos(card: ContentCard): Promise<void> {
    if (!this.config.enablePeoplePhotoPreloading || !card.people) {
      return;
    }

    const allPeople = [
      ...card.people.cast,
      ...card.people.directors,
      ...card.people.writers,
    ];

    const imageUrls = allPeople
      .filter(person => person.imageUrl)
      .map(person => person.imageUrl!)
      .slice(0, 4); // Only preload first 4 images

    const preloadPromises = imageUrls.map(url => this.preloadImage(url));
    
    try {
      await Promise.allSettled(preloadPromises);
    } catch (error) {
      console.warn('[ContentCardOptimizer] Preloading failed:', error);
    }
  }

  private preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already cached
      if (globalImageCache.get(url)) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        globalImageCache.set(url, img);
        resolve();
      };
      img.onerror = () => {
        reject(new Error(`Failed to preload: ${url}`));
      };
      img.src = url;
    });
  }

  /**
   * Gets performance statistics for content cards
   */
  public getStats(): {
    preloadedCards: number;
    maxPreloadedCards: number;
    cacheStats: ReturnType<typeof globalImageCache.getStats>;
  } {
    return {
      preloadedCards: this.preloadedCards.size,
      maxPreloadedCards: this.config.maxPreloadedCards,
      cacheStats: globalImageCache.getStats(),
    };
  }

  /**
   * Cleans up resources
   */
  public cleanup(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.preloadedCards.clear();
  }
}

/**
 * Utility function to extract people photos from content cards efficiently
 */
export function extractPeoplePhotos(cards: ContentCard[]): PersonSummaryWithPhoto[] {
  const allPeople: PersonSummaryWithPhoto[] = [];
  const seenIds = new Set<string>();

  for (const card of cards) {
    if (!card.people) continue;

    const cardPeople = [
      ...card.people.cast,
      ...card.people.directors,
      ...card.people.writers,
    ];

    for (const person of cardPeople) {
      if (!seenIds.has(person.id)) {
        seenIds.add(person.id);
        allPeople.push(person);
      }
    }
  }

  return allPeople;
}

/**
 * Utility function to prioritize people photos by importance
 */
export function prioritizePeoplePhotos(people: PersonSummaryWithPhoto[]): PersonSummaryWithPhoto[] {
  return people.sort((a, b) => {
    // Prioritize people with images
    if (a.imageUrl && !b.imageUrl) return -1;
    if (!a.imageUrl && b.imageUrl) return 1;

    // Prioritize by role (cast > directors > writers)
    const roleOrder = { cast: 0, director: 1, writer: 2 };
    const aRole = roleOrder[a.role as keyof typeof roleOrder] ?? 3;
    const bRole = roleOrder[b.role as keyof typeof roleOrder] ?? 3;
    
    if (aRole !== bRole) return aRole - bRole;

    // Alphabetical by name as final tiebreaker
    return a.name.localeCompare(b.name);
  });
}

/**
 * Utility function to batch process content cards for optimal performance
 */
export function batchProcessContentCards(
  cards: ContentCard[],
  batchSize: number = 5,
  processor: (batch: ContentCard[]) => Promise<void>
): Promise<void[]> {
  const batches: ContentCard[][] = [];
  
  for (let i = 0; i < cards.length; i += batchSize) {
    batches.push(cards.slice(i, i + batchSize));
  }

  return Promise.all(batches.map(batch => processor(batch)));
}

/**
 * Performance monitoring for content card rendering
 */
export class ContentCardPerformanceMonitor {
  private renderTimes = new Map<string, number>();
  private startTimes = new Map<string, number>();

  public startRender(cardId: string): void {
    this.startTimes.set(cardId, performance.now());
  }

  public endRender(cardId: string): void {
    const startTime = this.startTimes.get(cardId);
    if (startTime) {
      const renderTime = performance.now() - startTime;
      this.renderTimes.set(cardId, renderTime);
      this.startTimes.delete(cardId);
    }
  }

  public getAverageRenderTime(): number {
    const times = Array.from(this.renderTimes.values());
    if (times.length === 0) return 0;
    return times.reduce((sum, time) => sum + time, 0) / times.length;
  }

  public getSlowCards(threshold: number = 100): Array<{ cardId: string; renderTime: number }> {
    return Array.from(this.renderTimes.entries())
      .filter(([, time]) => time > threshold)
      .map(([cardId, renderTime]) => ({ cardId, renderTime }))
      .sort((a, b) => b.renderTime - a.renderTime);
  }

  public clear(): void {
    this.renderTimes.clear();
    this.startTimes.clear();
  }

  public getStats(): {
    totalCards: number;
    averageRenderTime: number;
    slowCards: number;
    fastestCard: number;
    slowestCard: number;
  } {
    const times = Array.from(this.renderTimes.values());
    
    return {
      totalCards: times.length,
      averageRenderTime: this.getAverageRenderTime(),
      slowCards: times.filter(time => time > 100).length,
      fastestCard: times.length > 0 ? Math.min(...times) : 0,
      slowestCard: times.length > 0 ? Math.max(...times) : 0,
    };
  }
}

// Global instances
export const globalContentCardOptimizer = new ContentCardOptimizer();
export const globalContentCardMonitor = new ContentCardPerformanceMonitor();

/**
 * React hook-friendly wrapper for content card optimization
 */
export function useContentCardOptimization(config?: Partial<ContentCardPerformanceConfig>) {
  const optimizer = new ContentCardOptimizer(config);
  const monitor = new ContentCardPerformanceMonitor();

  return {
    observeCard: optimizer.observeCard.bind(optimizer),
    unobserveCard: optimizer.unobserveCard.bind(optimizer),
    preloadCardPeoplePhotos: optimizer.preloadCardPeoplePhotos.bind(optimizer),
    startRender: monitor.startRender.bind(monitor),
    endRender: monitor.endRender.bind(monitor),
    getStats: () => ({
      optimizer: optimizer.getStats(),
      monitor: monitor.getStats(),
    }),
    cleanup: () => {
      optimizer.cleanup();
      monitor.clear();
    },
  };
}